import React from 'react'
import { FileText, BarChart3, User, Link as LinkIcon, Trash2, Edit3 } from 'lucide-react'
import { AnalysisResultView } from '../pages/AnalysisResultView'
import { CausalChainAnalysis } from './CausalChainAnalysis'
import { MultiOptionAnalysis } from './MultiOptionAnalysis'


interface AnalysisHistoryDetailProps {
  history: any
  onDelete?: (id: string) => void
  onEdit?: (id: string) => void
}

export const AnalysisHistoryDetail: React.FC<AnalysisHistoryDetailProps> = ({
  history,
  onDelete,
  onEdit
}) => {
  if (!history) {
    return (
      <div className="text-center py-8">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No details found</p>
      </div>
    )
  }

  const getAnalysisType = (result: any) => {
    if (result && 'recommendedOption' in result) {
      return 'pros-cons'
    } else if (result && 'personalizedInsights' in result) {
      return 'context'
    } else if (result && 'options' in result && Array.isArray(result.options)) {
      return 'multi-option'
    } else if (result && 'children' in result) {
      return 'causal-chain'
    }
    return 'unknown'
  }

  const getAnalysisTypeInfo = (type: string) => {
    switch (type) {
      case 'pros-cons':
        return { name: 'Pros & Cons Analysis', icon: '⚖️', color: 'bg-blue-100 text-blue-800' }
      case 'context':
        return { name: 'Context Analysis', icon: '👤', color: 'bg-purple-100 text-purple-800' }
      case 'multi-option':
        return { name: 'Multi-option Analysis', icon: '📊', color: 'bg-green-100 text-green-800' }
      case 'causal-chain':
        return { name: 'Causal Chain Analysis', icon: '🔗', color: 'bg-orange-100 text-orange-800' }
      default:
        return { name: 'Unknown Analysis', icon: '❓', color: 'bg-gray-100 text-gray-800' }
    }
  }

  const analysisType = getAnalysisType(history.result)
  const typeInfo = getAnalysisTypeInfo(analysisType)

  const renderAnalysisResult = () => {
    if (!history.result) {
      return (
        <div className="text-gray-400 text-center py-4">
          No analysis result available
        </div>
      )
    }

    switch (analysisType) {
      case 'pros-cons':
        return <AnalysisResultView analysis={history.result} />
      
      case 'context':
        // ContextAnalysis component needs different props, showing simplified result here
        return (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-purple-900 mb-2">Context Analysis Result</h3>
            <div className="text-purple-800">
              {history.result.personalizedInsights && (
                <div className="mb-4">
                  <h4 className="font-medium mb-2">Personalized Insights:</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {history.result.personalizedInsights.map((insight: string, i: number) => (
                      <li key={i}>{insight}</li>
                    ))}
                  </ul>
                </div>
              )}
              {history.result.contextualFactors && (
                <div>
                  <h4 className="font-medium mb-2">Contextual Factors:</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {history.result.contextualFactors.map((factor: string, i: number) => (
                      <li key={i}>{factor}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )
      
      case 'multi-option':
        return <MultiOptionAnalysis result={history.result} />
      
      case 'causal-chain':
        return <CausalChainAnalysis result={history.result} isLoading={false} />
      
      default:
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap">
              {JSON.stringify(history.result, null, 2)}
            </pre>
          </div>
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-2xl">{typeInfo.icon}</span>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${typeInfo.color}`}>
              {typeInfo.name}
            </span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{history.title}</h2>
          <p className="text-gray-700 mb-4">{history.description}</p>
          <div className="text-sm text-gray-500">
            Created on {new Date(history.createdAt || history.created_at).toLocaleDateString()} at{' '}
            {new Date(history.createdAt || history.created_at).toLocaleTimeString()}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex space-x-2">
          {onEdit && (
            <button
              onClick={() => onEdit(history.id)}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              title="Edit analysis"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(history.id)}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete analysis"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Options */}
      {history.options && history.options.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Options
          </h4>
          <ul className="space-y-1">
            {history.options.map((opt: any, i: number) => (
              <li key={i} className="text-sm text-gray-700 flex items-start">
                <span className="text-gray-400 mr-2">•</span>
                <span>{opt.text || opt}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Criteria */}
      {history.criteria && history.criteria.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <FileText className="w-4 h-4 mr-2" />
            Criteria
          </h4>
          <ul className="space-y-1">
            {history.criteria.map((c: any, i: number) => (
              <li key={i} className="text-sm text-gray-700 flex items-start">
                <span className="text-gray-400 mr-2">•</span>
                <span>
                  {c.name || c} 
                  {c.weight && ` (weight: ${c.weight})`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Context Profile */}
      {history.context_profile && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <User className="w-4 h-4 mr-2" />
            Context Profile
          </h4>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-700">
              <div><strong>Name:</strong> {history.context_profile.name}</div>
              {history.context_profile.description && (
                <div><strong>Description:</strong> {history.context_profile.description}</div>
              )}
              {history.context_profile.preferences && (
                <div>
                  <strong>Preferences:</strong>
                  <ul className="ml-4 mt-1">
                    <li>Risk Tolerance: {history.context_profile.preferences.riskTolerance}</li>
                    <li>Time Horizon: {history.context_profile.preferences.timeHorizon}</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analysis Result */}
      <div>
        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
          <LinkIcon className="w-4 h-4 mr-2" />
          Analysis Result
        </h4>
        {renderAnalysisResult()}
      </div>
    </div>
  )
} 